import { useState, FC } from 'react';
import Alert from '@mui/material/Alert';
import { useSnackbar } from 'notistack';
import { Box, Fade, ButtonGroup, Button, Typography } from '@mui/material';
import { Close, Restore } from '@mui/icons-material';
import { Modal, Loader } from '../../../components';
import { resetBuild } from '../../../fetch';
import { IBuild } from '../../../models';

interface IResetBuildModal {
  currentBuild: IBuild;
  open: boolean;
  onClose: () => void;
  fetchBuilds: () => void;
}

export const ResetBuildModal: FC<IResetBuildModal> = ({
  open,
  onClose,
  currentBuild,
  fetchBuilds
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isResetting, setResetting] = useState(false);
  return (
    <Modal open={open} onClose={onClose} maxWidth="sm">
      <Fade in={open}>
        <div>
          {isResetting && (
            <Loader position="centered" type="overlay">
              Resetting...
            </Loader>
          )}
          <Box marginBottom="1rem">
            {currentBuild && (
              <Typography variant="h5">
                {currentBuild?.jobType ? `Reset ${currentBuild.jobType} Build` : 'Reset Build'}{' '}
                {currentBuild?.neighborhood} - Lot {currentBuild?.lotNumber}{' '}
                {currentBuild?.status ? `- ${currentBuild?.status}` : ''}{' '}
                {currentBuild?.buyer ? `- ${currentBuild?.buyer}` : ''}
              </Typography>
            )}
          </Box>
          <Alert severity="warning">
            This function will reset a build back to the "New" status. It will also delete any
            contract documents or offers assigned to the build. Finally it will remove any options
            that were selected after contract in case those should have been contract options.
          </Alert>
          <Box mt={1} display="flex" alignItems="center" justifyContent="flex-end">
            <ButtonGroup>
              <Button color="inherit" variant="contained" startIcon={<Close />} onClick={onClose}>
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                disabled={isResetting}
                startIcon={<Restore />}
                onClick={async () => {
                  setResetting(true);
                  try {
                    await resetBuild(currentBuild.buildId);
                    enqueueSnackbar(`Build reset!`, {
                      variant: 'success'
                    });
                    onClose();
                    fetchBuilds();
                  } catch (error) {
                    enqueueSnackbar(error?.Detail ?? 'Error resetting build, please try again.', {
                      variant: 'error'
                    });
                  } finally {
                    setResetting(false);
                  }
                }}
              >
                Reset
              </Button>
            </ButtonGroup>
          </Box>
        </div>
      </Fade>
    </Modal>
  );
};
